import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'
import { cn } from '../../lib/utils'

/**
 * A single-choice toggle group with a highlight that slides from the option
 * that was picked to the one being picked.
 *
 * This is animate-ui's radix toggle-group behaviour rebuilt on the
 * `@radix-ui/react-toggle-group` already in the project, for the same reason
 * `wheel-picker.tsx` is a rewrite of its registry item: the published component
 * assumes Tailwind v4 and pulls in `motion` plus the `radix-ui` umbrella
 * package, which would put a second copy of the primitives in the bundle. The
 * highlight is one absolutely-positioned div measured off the selected item and
 * moved with a CSS transition — no animation library involved.
 */

// Built off the div props rather than the primitive's: Root's own type is a
// union of the single and multiple modes, and this component is only ever the
// first of the two.
interface AnimatedToggleGroupProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onChange' | 'defaultValue' | 'dir'> {
  value: string
  onValueChange: (value: string) => void
  /**
   * What pressing the option **already chosen** means, where it means anything.
   *
   * A choice with no empty state ignores that press, which is the default. Give
   * this to a group whose answer is already made and where pressing it again is
   * the natural way to say "done with this" — the hold panel, where it puts the
   * hold down.
   */
  onEmpty?: () => void
  disabled?: boolean
  /**
   * Let the options run onto more than one line and size to their text, for a
   * set too long to read across a phone. One row of equal columns otherwise.
   */
  wrap?: boolean
  /**
   * Let the options keep their own width on one line, for a row of icons that
   * are already square and would only be stretched by a column each.
   */
  fit?: boolean
  /**
   * What the sliding highlight is painted with, for a choice whose options have
   * a colour of their own — a hold's role, say, where the pill that slides
   * between them carrying that colour is most of what says which one is picked.
   */
  highlightClassName?: string
  highlightStyle?: React.CSSProperties
}

interface HighlightBox {
  left: number
  top: number
  width: number
  height: number
}

export function AnimatedToggleGroup({
  value,
  onValueChange,
  onEmpty,
  wrap = false,
  fit = false,
  highlightClassName,
  highlightStyle,
  className,
  children,
  ...props
}: AnimatedToggleGroupProps) {
  const listRef = useRef<HTMLDivElement>(null)
  const [highlight, setHighlight] = useState<HighlightBox | null>(null)
  const [ready, setReady] = useState(false)

  const measure = useCallback(() => {
    const list = listRef.current
    if (!list) return
    const selected = list.querySelector<HTMLElement>('[data-state="on"]')
    if (!selected) return setHighlight(null)
    setHighlight({
      left: selected.offsetLeft,
      top: selected.offsetTop,
      width: selected.offsetWidth,
      height: selected.offsetHeight,
    })
  }, [])

  // Before paint, so the highlight is never a frame late or a frame wrong.
  useLayoutEffect(measure, [measure, value, children, wrap])

  // The first placement is not a move, so it must not be animated: the
  // highlight would otherwise slide in from the corner on mount.
  useLayoutEffect(() => {
    if (highlight && !ready) setReady(true)
  }, [highlight, ready])

  useLayoutEffect(() => {
    const list = listRef.current
    if (!list || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(list)
    return () => observer.disconnect()
  }, [measure])

  return (
    <ToggleGroupPrimitive.Root
      ref={listRef}
      type="single"
      value={value}
      // Radix hands back "" when the pressed item is toggled off. A choice with
      // no empty state ignores that and stays where it was; one that has
      // something to do with it says so with `onEmpty`.
      onValueChange={(next) => (next ? onValueChange(next) : onEmpty?.())}
      className={cn(
        'relative isolate flex w-full items-center gap-0.5 rounded-lg border bg-muted/50 p-0.5',
        // One row of equal columns unless the options are allowed to wrap, in
        // which case each takes the width of its own label — or to keep their
        // own width on one line, which is what a row of icons wants.
        wrap ? 'flex-wrap' : fit ? '' : '[&>button]:flex-1',
        className
      )}
      {...props}
    >
      {highlight && (
        <span
          aria-hidden
          className={cn(
            'absolute -z-10 rounded-md bg-background shadow-sm',
            ready && 'transition-all duration-200 ease-out motion-reduce:transition-none',
            highlightClassName
          )}
          style={{
            left: highlight.left,
            top: highlight.top,
            width: highlight.width,
            height: highlight.height,
            ...highlightStyle,
          }}
        />
      )}
      {children}
    </ToggleGroupPrimitive.Root>
  )
}

export function AnimatedToggleGroupItem({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item>) {
  return (
    <ToggleGroupPrimitive.Item
      className={cn(
        'min-w-0 shrink-0 rounded-md px-2.5 py-1.5 text-sm transition-colors',
        'text-muted-foreground hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        'data-[state=on]:font-medium data-[state=on]:text-foreground',
        'disabled:pointer-events-none disabled:opacity-50',
        className
      )}
      {...props}
    />
  )
}
